import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useVenue } from "../VenueProvider";
import { useAuth } from "../AuthProvider";
import { Badge } from "../badge";
import { Alert, AlertTitle, AlertDescription, AlertActions } from "../alert";
import { Button } from "../button";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import {
  crearUnidadesEnLote,
  ESTADO_UNIDAD_LABELS,
  ESTADOS_UNIDAD,
} from "../../utils/inventario";
import { eventosFuturosConUnidad } from "../../utils/unidades";
import {
  esItemizado,
  esPool,
  piezasTotalesDe,
  piezasReservablesDe,
  noDisponiblesDe,
  totalNoDisponible,
  unidadDe,
  escalaDe,
  tieneEscala,
  nivelesDe,
  nivelLabel,
  nivelLlenoDe,
  esNivelValido,
  estaVacia,
} from "../../utils/catalogo";
import {
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const ESTADO_COLOR = { activo: "emerald", mantenimiento: "amber", baja: "zinc" };


const Card = ({ title, action, children }) => (
  <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm">
    <div className="flex items-center gap-2 mb-3">
      <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
        {title}
      </span>
      {action && <div className="ml-auto">{action}</div>}
    </div>
    {children}
  </div>
);

// Las piezas físicas de un servicio del catálogo: darlas de alta, ver su
// estado y para qué evento están reservadas. Viven en la subcolección
// `unidades` de conceptos/{id}; las escrituras están en utils/inventario.js.
// El nivel de cada pieza se mide con la escala que define el propio servicio
// (FRA-351): sin escala, no hay nivel que mostrar.
//
// Aquí hubo un inventario de consumibles (existencias, consumo imputado,
// recargas) y se retiró: lo que se compra o se repone es un gasto ligado al
// servicio — ver el comentario de utils/inventario.js.
// `soloLectura` (inversionista, P-FRA-13): piezas con su estado y nivel a la
// vista, sin alta en lote, borrado ni cambio de estado/nivel.
const ServicioInventario = ({ item, venueId, soloLectura = false }) => {
  const navigate = useNavigate();
  const { events } = useVenue();
  const { userInfo, user } = useAuth();

  const [unidades, setUnidades] = useState([]);
  const [lote, setLote] = useState({
    cantidad: "5",
    plantilla: "",
    desde: "1",
    sku: "",
    ubicacion: "",
  });
  const [showLote, setShowLote] = useState(false);
  const [savingLote, setSavingLote] = useState(false);
  const [unidadPendiente, setUnidadPendiente] = useState(null);
  const [avisoMantenimiento, setAvisoMantenimiento] = useState(null);

  const itemizado = esItemizado(item);
  const conEscala = tieneEscala(item);
  const escalaPorcentaje = escalaDe(item)?.tipo === "porcentaje";

  useEffect(() => {
    if (!venueId || !item?.id) return;
    const base = doc(db, "venues", venueId, "conceptos", item.id);
    const unsubs = [];
    if (!itemizado) setUnidades([]);
    if (itemizado) {
      unsubs.push(
        onSnapshot(collection(base, "unidades"), (snap) =>
          setUnidades(
            snap.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .sort((a, b) => (a.etiqueta || "").localeCompare(b.etiqueta || "", "es", { numeric: true }))
          )
        )
      );
    }
    return () => unsubs.forEach((u) => u());
  }, [venueId, item?.id, itemizado]);

  const log = (action, entityId, entityName, details, entity = "servicio") =>
    logBitacora(
      venueId,
      { action, entity, entityId, entityName, details },
      userInfo,
      user
    );


  const handleLote = async () => {
    const cantidad = Number(lote.cantidad);
    if (!cantidad || cantidad <= 0 || cantidad > 200) {
      showAlert("Ingresa cuántas piezas crear (máximo 200).", "error");
      return;
    }
    setSavingLote(true);
    try {
      const plantilla = lote.plantilla.trim() || `${item.nombre} {n}`;
      const creadas = await crearUnidadesEnLote({
        venueId,
        itemId: item.id,
        cantidad,
        plantilla,
        desde: Number(lote.desde) || 1,
        skuPrefijo: lote.sku.trim(),
        ubicacion: lote.ubicacion.trim(),
        // Las piezas nacen en el primer escalón de la escala del servicio
        // (o sin nivel, si no mide nivel).
        nivelInicial: nivelLlenoDe(item),
        user,
      });
      // Eco local: se pintan de inmediato y el snapshot del servidor las
      // reconcilia después (dedupe por id), para que la lista no se quede
      // "vacía" mientras confirma la red.
      setUnidades((prev) => {
        const porId = new Map(prev.map((u) => [u.id, u]));
        creadas.forEach((u) => {
          if (!porId.has(u.id)) porId.set(u.id, u);
        });
        return [...porId.values()].sort((a, b) =>
          (a.etiqueta || "").localeCompare(b.etiqueta || "", "es", { numeric: true })
        );
      });
      log(BITACORA_ACTIONS.SERVICIO_UNIDAD_CREATE, item.id, item.nombre, {
        cantidad: creadas.length,
        plantilla,
      });
      setShowLote(false);
      showAlert(
        creadas.length === 1 ? "1 pieza creada" : `${creadas.length} piezas creadas`,
        "success"
      );
    } catch (err) {
      showAlert(`No se pudieron crear. [${err.message}]`, "error");
    } finally {
      setSavingLote(false);
    }
  };

  const patchUnidad = async (unidad, patch, action, details) => {
    try {
      await updateDoc(
        doc(db, "venues", venueId, "conceptos", item.id, "unidades", unidad.id),
        { ...patch, updatedAt: serverTimestamp(), updatedBy: user?.uid || null }
      );
      log(action, unidad.id, unidad.etiqueta || "", details, "servicio_unidad");
    } catch (err) {
      showAlert(`No se pudo actualizar la pieza. [${err.message}]`, "error");
    }
  };

  // El nivel se guarda como número en escalas por porcentaje y como texto
  // en escalas por palabra; el select siempre entrega texto.
  const cambiarNivelUnidad = (unidad, valorTexto) => {
    if (valorTexto === "") return;
    const nivel = escalaPorcentaje ? Number(valorTexto) : valorTexto;
    if (!esNivelValido(item, nivel)) return;
    patchUnidad(unidad, { nivel }, BITACORA_ACTIONS.SERVICIO_UNIDAD_EDIT, { nivel });
  };

  const cambiarEstadoUnidad = (unidad, estado) => {
    // No se bloquea el cambio: el owner sabe cosas que el sistema no. Pero
    // si la unidad ya está comprometida en eventos por venir, se le dice
    // exactamente a cuáles les va a faltar.
    if (estado !== "activo") {
      const futuros = eventosFuturosConUnidad({ unidadId: unidad.id, events });
      if (futuros.length > 0) setAvisoMantenimiento({ unidad, estado, futuros });
    }
    patchUnidad(unidad, { estado }, BITACORA_ACTIONS.SERVICIO_UNIDAD_EDIT, { estado });
  };

  const borrarUnidad = async () => {
    const unidad = unidadPendiente;
    if (!unidad) return;
    try {
      await deleteDoc(
        doc(db, "venues", venueId, "conceptos", item.id, "unidades", unidad.id)
      );
      log(BITACORA_ACTIONS.SERVICIO_UNIDAD_DELETE, unidad.id, unidad.etiqueta || "", {}, "servicio_unidad");
      showAlert("Pieza eliminada", "success");
    } catch (err) {
      showAlert(`No se pudo eliminar. [${err.message}]`, "error");
    } finally {
      setUnidadPendiente(null);
    }
  };


  const inputClass =
    "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

  if (!itemizado) {
    return (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm text-sm text-zinc-500">
        Este servicio no lleva piezas. Actívalas en Editar para darlas de alta,
        seguirles el estado y reservarlas para un evento.
      </div>
    );
  }

  // Modo por cantidad: no hay piezas que listar, hay un conteo. Los números se
  // editan en el formulario del servicio (son parte de cómo está configurado),
  // no aquí: tener dos lugares para el mismo total es tener dos verdades.
  if (esPool(item)) {
    const total = piezasTotalesDe(item);
    const fuera = totalNoDisponible(item);
    const reservables = piezasReservablesDe(item);
    const renglones = noDisponiblesDe(item);
    return (
      <div className="space-y-5">
        <Card title="Cuántas hay">
          <div className="grid grid-cols-3 divide-x divide-zinc-100 dark:divide-zinc-700/40 text-center">
            <div className="px-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
                Totales
              </div>
              <div className="mt-0.5 text-lg font-bold text-zinc-900 dark:text-white">{total}</div>
            </div>
            <div className="px-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
                No disponibles
              </div>
              <div className="mt-0.5 text-lg font-bold text-amber-600">{fuera || "—"}</div>
            </div>
            <div className="px-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
                Reservables
              </div>
              <div className="mt-0.5 text-lg font-bold text-emerald-600">{reservables}</div>
            </div>
          </div>
          {renglones.length > 0 && (
            <ul className="mt-4 divide-y divide-zinc-100 dark:divide-zinc-700/40">
              {renglones.map((r) => (
                <li key={r.id || r.nombre} className="flex items-center gap-3 py-2 text-sm">
                  <span className="min-w-0 flex-1 truncate text-zinc-700 dark:text-zinc-300">
                    {r.nombre}
                  </span>
                  <span className="text-zinc-500 whitespace-nowrap">
                    {r.cantidad} {unidadDe(item)}
                    {r.cantidad === 1 ? "" : "s"}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-xs text-zinc-500">
            Estos números se editan en <strong>Editar</strong>, junto con el resto de
            cómo está configurado el servicio. Cuántas están ocupadas no se captura:
            sale de los eventos que las tienen apartadas — está en Reservas.
          </p>
        </Card>
      </div>
    );
  }


  return (
    <div className="space-y-5">
      {/* Piezas físicas */}
      {itemizado && (
        <Card
          title="Piezas"
          action={
            soloLectura ? null : (
            <button
              type="button"
              onClick={() => setShowLote(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold text-white transition hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Agregar piezas
            </button>
            )
          }
        >
          {unidades.length === 0 ? (
            <p className="text-sm text-zinc-400">
              {soloLectura
                ? "Todavía no hay piezas registradas."
                : "Todavía no hay piezas. Créalas en lote para no capturarlas una por una."}
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {unidades.map((u) => (
                <div
                  key={u.id}
                  className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-sm text-zinc-900 dark:text-white truncate">
                        {u.etiqueta}
                      </div>
                      {(u.sku || u.ubicacion) && (
                        <div className="text-xs text-zinc-500 truncate">
                          {[u.sku, u.ubicacion].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                    {!soloLectura && (
                    <button
                      type="button"
                      onClick={() => setUnidadPendiente(u)}
                      className="text-zinc-400 hover:text-red-500 p-1"
                      title="Eliminar pieza"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <Badge color={ESTADO_COLOR[u.estado || "activo"]}>
                      {ESTADO_UNIDAD_LABELS[u.estado || "activo"]}
                    </Badge>
                    {/* El nivel solo existe si el servicio define una escala
                        (FRA-351). Vacía = último escalón: por recargar. */}
                    {conEscala && nivelLabel(item, u.nivel) && (
                      <Badge color={estaVacia(item, u.nivel) ? "red" : "emerald"}>
                        {nivelLabel(item, u.nivel)}
                        {estaVacia(item, u.nivel) ? " · por recargar" : ""}
                      </Badge>
                    )}
                  </div>
                  {!soloLectura && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <select
                      value={u.estado || "activo"}
                      onChange={(e) => cambiarEstadoUnidad(u, e.target.value)}
                      className="rounded-md border border-zinc-200 dark:border-zinc-700 bg-transparent px-1.5 py-0.5 text-xs text-zinc-600 dark:text-zinc-300"
                    >
                      {ESTADOS_UNIDAD.map((e) => (
                        <option key={e} value={e}>{ESTADO_UNIDAD_LABELS[e]}</option>
                      ))}
                    </select>
                    {conEscala && (
                      <select
                        value={esNivelValido(item, u.nivel) && u.nivel != null ? String(u.nivel) : ""}
                        onChange={(e) => cambiarNivelUnidad(u, e.target.value)}
                        className="rounded-md border border-zinc-200 dark:border-zinc-700 bg-transparent px-1.5 py-0.5 text-xs text-zinc-600 dark:text-zinc-300"
                      >
                        {!(esNivelValido(item, u.nivel) && u.nivel != null) && (
                          <option value="">Nivel…</option>
                        )}
                        {nivelesDe(item).map((n) => (
                          <option key={String(n)} value={String(n)}>{nivelLabel(item, n)}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Alta de piezas en lote — dialog propio, con vista previa */}
      {showLote && !soloLectura && (() => {
        const plantillaEfectiva = lote.plantilla.trim() || `${item?.nombre || "Pieza"} {n}`;
        const etiqueta = (num) => plantillaEfectiva.replace(/\{n\}/g, String(num));
        const n = Number(lote.cantidad) || 0;
        const desde = Number(lote.desde) || 1;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/30" onClick={() => setShowLote(false)} />
            <div className="relative w-full max-w-md rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900">
              <div className="flex items-start gap-2 px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
                <div>
                  <h2 className="text-base font-semibold text-zinc-900 dark:text-white">
                    Agregar piezas
                  </h2>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    Crea varias piezas a la vez, cada una con su etiqueta numerada.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowLote(false)}
                  className="ml-auto text-zinc-400 hover:text-zinc-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">
                      ¿Cuántas?
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="200"
                      value={lote.cantidad}
                      onChange={(e) => setLote({ ...lote, cantidad: e.target.value })}
                      className={inputClass}
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">
                      Numerar desde
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={lote.desde}
                      onChange={(e) => setLote({ ...lote, desde: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">
                    Etiqueta de cada pieza
                  </label>
                  <input
                    type="text"
                    value={lote.plantilla}
                    onChange={(e) => setLote({ ...lote, plantilla: e.target.value })}
                    placeholder={plantillaEfectiva}
                    className={inputClass}
                  />
                  <p className="mt-1 text-xs text-zinc-500">
                    {"{n}"} se reemplaza por el número de cada pieza.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">
                      Prefijo de SKU (opcional)
                    </label>
                    <input
                      type="text"
                      value={lote.sku}
                      onChange={(e) => setLote({ ...lote, sku: e.target.value })}
                      placeholder="CAL-"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">
                      Ubicación (opcional)
                    </label>
                    <input
                      type="text"
                      value={lote.ubicacion}
                      onChange={(e) => setLote({ ...lote, ubicacion: e.target.value })}
                      placeholder="Bodega 2"
                      className={inputClass}
                    />
                  </div>
                </div>
                {/* Vista previa: enseñar el resultado vale más que explicar
                    la plantilla. */}
                {n > 0 && (
                  <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/40 px-3 py-2 text-xs text-zinc-600 dark:text-zinc-300">
                    Se {n === 1 ? "creará 1 pieza" : `crearán ${n} piezas`}:{" "}
                    <span className="font-medium text-zinc-900 dark:text-white">
                      {etiqueta(desde)}
                    </span>
                    {n > 1 && (
                      <>
                        {" "}…{" "}
                        <span className="font-medium text-zinc-900 dark:text-white">
                          {etiqueta(desde + n - 1)}
                        </span>
                      </>
                    )}
                    {lote.sku.trim() ? ` · SKU ${lote.sku.trim()}${desde}…` : ""}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-zinc-200 dark:border-zinc-700/60">
                <button
                  type="button"
                  onClick={() => setShowLote(false)}
                  className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-700"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleLote}
                  disabled={savingLote || !(n > 0)}
                  className="px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: ACCENT }}
                >
                  {savingLote
                    ? "Creando…"
                    : n > 0
                    ? `Crear ${n === 1 ? "1 pieza" : `${n} piezas`}`
                    : "Crear"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Borrar pieza */}
      <Alert open={!!unidadPendiente && !soloLectura} onClose={() => setUnidadPendiente(null)}>
        <AlertTitle>¿Eliminar esta pieza?</AlertTitle>
        <AlertDescription>
          {unidadPendiente?.etiqueta}. Si solo está fuera de servicio, mejor
          márcala como de baja para conservar su historial.
        </AlertDescription>
        <AlertActions>
          <Button plain onClick={() => setUnidadPendiente(null)}>Cancelar</Button>
          <Button color="red" onClick={borrarUnidad}>Eliminar</Button>
        </AlertActions>
      </Alert>

      {/* Aviso de eventos afectados */}
      <Alert open={!!avisoMantenimiento} onClose={() => setAvisoMantenimiento(null)}>
        <AlertTitle>
          <span className="inline-flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
            Hay eventos que la tienen asignada
          </span>
        </AlertTitle>
        <AlertDescription>
          {avisoMantenimiento?.unidad?.etiqueta} sigue asignada a estos eventos
          por venir. El cambio de estado ya se guardó; revisa si hay que
          sustituirla.
        </AlertDescription>
        <ul className="mt-2 ml-5 list-disc text-sm text-zinc-600 dark:text-zinc-300">
          {(avisoMantenimiento?.futuros || []).map((ev) => (
            <li key={ev.id}>
              <button
                type="button"
                onClick={() => navigate(`/dashboard/eventos/${ev.id}`)}
                className="hover:underline"
                style={{ color: ACCENT }}
              >
                {ev.nombre || "Sin nombre"}
              </button>
            </li>
          ))}
        </ul>
        <AlertActions>
          <Button onClick={() => setAvisoMantenimiento(null)}>Entendido</Button>
        </AlertActions>
      </Alert>
    </div>
  );
};

export default ServicioInventario;
